<template>
  <v-layout column fill-height>
    <v-row class="page-header-block">
      <h1 class="page-header-block__header">{{pageTitle}}</h1>

      <v-spacer></v-spacer>

      <import-xml></import-xml>

      <v-btn class="ml-2" @click="save">
        <span class="mr-2">Save</span>
      </v-btn>
    </v-row>

    <div class="page-body-block">
      <div class="page-menu-block">
        <div class="page-menu-block__sub-block">
          <div class="tree-wrapper">
            <v-treeview activatable :items="treeItems" :open="openItems">
              <template v-slot:prepend="{ item }">
                <v-icon v-if="item.icon === 'il'">$vuetify.icons.il</v-icon>
                <v-icon v-if="item.icon === 'st'">$vuetify.icons.st</v-icon>
                <v-icon v-if="item.icon === 'fbd'">$vuetify.icons.fbd</v-icon>
                <v-icon v-if="item.icon === 'ld'">$vuetify.icons.ld</v-icon>
                <v-icon v-if="item.icon === 'sfc'">$vuetify.icons.sfc</v-icon>
                <v-icon v-if="item.icon === 'p'">$vuetify.icons.p</v-icon>
                <v-icon v-if="item.icon === 'r'">$vuetify.icons.r</v-icon>
                <v-icon v-if="item.icon === 'fb'">$vuetify.icons.fb</v-icon>
                <v-icon v-if="item.icon === 't'">$vuetify.icons.t</v-icon>
                <v-icon v-if="item.icon === 'f'">$vuetify.icons.f</v-icon>
              </template>

              <template v-slot:label="{ item }">
                <div @click="selectTreeNode(item)" @contextmenu="treeItemRightClick">{{ item.name }}</div>
              </template>

              <template v-slot:append="{ item }">
                <div v-if="item.type">
                  <v-icon small class="mr-1" @click="editTreeItem(item)">mdi-pencil</v-icon>
                  <v-icon small @click="deleteTreeItem(item)">mdi-delete</v-icon>
                </div>
              </template>
            </v-treeview>
          </div>

          <add-items></add-items>
        </div>
      </div>

      <div class="page-content-block">
        <component :is="openComponent" :id="treeSelectedId"></component>
      </div>
    </div>

    <v-snackbar color="error" top right v-model="showError">{{ errorMessage }}</v-snackbar>
  </v-layout>
</template>

<script>
import { find, filter, map, some, flatMap, concat } from "lodash";
import validationRules from "src/mixins/validationRules";
import uuid from "src/mixins/uuid";
import ImportXml from "src/components/addProject/ImportXml";
import AddItems from "src/components/addProject/addItems/Index";
import ProjectConfig from "src/components/addProject/projectConfig/Index";
import ResourceWindow from "src/components/addProject/resource/Index";
import PouWindow from "src/components/addProject/pou/Index";
import DataTypeWindow from "src/components/addProject/dataType/Index";
import http from "src/util/http";
import parseProject from "src/util/parseProject";

export default {
  name: "AddProject",
  components: {
    ImportXml,
    AddItems,
    ProjectConfig,
    ResourceWindow,
    PouWindow,
    DataTypeWindow,
  },
  props: ["id"],
  mixins: [validationRules, uuid],
  data() {
    return {
      openItems: ["project"],
      openComponent: "ProjectConfig",
      treeSelectedId: "",

      showError: false,
      errorMessage: "",
    };
  },
  computed: {
    pageTitle() {
      return this.id ? "edit project" : "add project";
    },
    treeItems() {
      const resourceItem = {
        name: "Resources",
        icon: "r",
        id: "resource",
        children: map(
          this.$store.state.configuration.resources,
          (resource) => ({
            ...resource,
            id: resource.tempId,
            icon: "r",
            type: "resource",
          })
        ),
      };

      const dataTypeItem = {
        name: "Data Types",
        icon: "t",
        id: "datatype",
        children: map(this.$store.state.dataTypes, (dataType) => ({
          name: dataType.name,
          icon: "t",
          id: dataType.tempId,
          type: "dataType",
        })),
      };

      const pouItems = map(this.$store.state.pous, (pou) => ({
        ...pou,
        id: pou.tempId,
        icon: this.calcPouIcon(pou.body.language),
        type: "pou",
      }));

      const programItem = {
        name: "Program",
        icon: "p",
        id: "program",
        children: filter(pouItems, (pouItem) => pouItem.pouType === "program"),
      };

      const functionItem = {
        name: "Function",
        icon: "f",
        id: "function",
        children: filter(pouItems, (pouItem) => pouItem.pouType === "function"),
      };

      const functionBlockItem = {
        name: "Function Block",
        icon: "fb",
        id: "functionblock",
        children: filter(
          pouItems,
          (pouItem) => pouItem.pouType === "functionBlock"
        ),
      };

      return [
        {
          id: "project",
          name: "Project",
          icon: "p",
          children: [resourceItem]
            .concat([dataTypeItem])
            .concat([programItem])
            .concat([functionItem])
            .concat([functionBlockItem]),
        },
      ];
    },
  },
  async mounted() {
    if (this.id) {
      this.$store.dispatch("updateShowLoading", true);

      const { data: project } = await http().get(`projects/${this.id}`);
      const parsedProject = parseProject(project);
      this.$store.dispatch("updateProject", parsedProject);

      this.$store.dispatch("updateShowLoading", false);
    }
  },
  watch: {
    async id(newValue) {
      this.$store.dispatch("clearProject");

      if (newValue) {
        const { data: project } = await http().get(`projects/${newValue}`);
        const parsedProject = parseProject(project);
        this.$store.dispatch("updateProject", parsedProject);
      }
    },
    showError(newValue) {
      if (!newValue) {
        this.errorMessage = "";
      }
    },
  },
  destroyed() {
    this.$store.dispatch("clearProject");
  },
  methods: {
    selectTreeNode({ id }) {
      if (id === "project") {
        this.openComponent = "ProjectConfig";
      } else if (
        find(this.$store.state.configuration.resources, { tempId: id })
      ) {
        this.openComponent = "ResourceWindow";
        this.treeSelectedId = id;
      } else if (find(this.$store.state.pous, { tempId: id })) {
        this.openComponent = "PouWindow";
        this.treeSelectedId = id;
      } else if (find(this.$store.state.dataTypes, { tempId: id })) {
        this.openComponent = "DataTypeWindow";
        this.treeSelectedId = id;
      }
    },
    treeItemRightClick(event) {
      event.preventDefault();
    },
    calcPouIcon(language) {
      return language.toLowerCase();
    },
    async save() {
      this.$store.dispatch("updateShowLoading", true);

      const { project } = this.$store.getters;

      if (this.id) {
        await http().put(`projects/${this.id}`, project);
      } else {
        const {
          data: { id },
        } = await http().post("projects", project);

        this.$router.push({ path: `/projects/${id}` });
      }

      this.$store.dispatch("updateShowLoading", false);
    },
    editTreeItem(item) {
      let componentName;

      switch (item.type) {
        case "resource":
          componentName = "AddResource";
          break;

        case "dataType":
          componentName = "AddDataType";
          break;

        case "pou":
          componentName = "AddPou";
          break;
      }

      this.$store.dispatch("updateAddItem", {
        componentName,
        itemTempId: item.id,
        showDialog: true,
      });
    },
    async deleteTreeItem(item) {
      if (item.type === "pou") {
        const pouName = find(this.$store.state.pous, { tempId: item.id }).name;

        if (
          some(this.$store.state.configuration.resources, (resource) => {
            return some(resource.pouInstances, { typeName: pouName });
          })
        ) {
          this.errorMessage = `POU ${pouName} is in used`;
          this.showError = true;
          return;
        } else if (
          some(this.$store.state.configuration.resources, (resource) =>
            some(resource.tasks, (task) =>
              some(task.pouInstances, { typeName: pouName })
            )
          )
        ) {
          this.errorMessage = `POU ${pouName} is in used`;
          this.showError = true;
          return;
        }
      } else if (item.type === "dataType") {
        const dataTypeName = find(this.$store.state.dataTypes, {
          tempId: item.id,
        }).name;

        const globalVars = this.$store.state.configuration.globalVars;
        const resourceGlobalVars = flatMap(
          this.$store.state.configuration.resources,
          "globalVars"
        );
        const pouVars = flatMap(this.$store.state.pous, "variables");

        const vars = concat(globalVars, resourceGlobalVars, pouVars);

        if (
          some(
            vars,
            ({ type }) =>
              type.name === "derived" && type.derivedName === dataTypeName
          )
        ) {
          this.errorMessage = `Type ${dataTypeName} is in used`;
          this.showError = true;
          return;
        }
      }

      if (await this.$confirm("Are you sure you want to delete this item?")) {
        if (this.treeSelectedId === item.id) {
          this.openComponent = "ProjectConfig";
        }

        if (item.type === "resource") {
          this.$store.dispatch("deleteResource", item.id);
        } else if (item.type === "dataType") {
          this.$store.dispatch("deleteDataType", item.id);
        } else if (item.type === "pou") {
          this.$store.dispatch("deletePou", item.id);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tree-wrapper {
  overflow-y: auto;
  height: 100%;
}

.page-menu-block {
  border-right: solid 1px #bdbdbd;
  padding: 0;
  min-width: 300px;

  &__sub-block {
    position: relative;
    border-bottom: solid 1px #bdbdbd;
    height: 50%;
  }
}

.page-content-block {
  display: flex;
  flex-direction: column;
  flex-grow: 2;
  padding-left: 10px;
}

.page-body-block {
  display: flex;
  flex-direction: row;
  height: 100%;
}
</style>
